import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, OnDestroy, SecurityContext, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { jwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, catchError, EMPTY, exhaustMap, filter, from, map, Observable, of, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';

import { APP_CONFIG, AuthStatus, DateTimeUtils, ENV_CONFIG, UtmData } from '@bn/primitives';
import { AuthStorage, UserAuth, UserJWT } from '@bn/shared/core/api';
import { PosthogService } from '@bn/shared/core/services';
import { BnToastrService } from '@bn/shared/toastr';

import { ApiKeyResponse } from '../models/api-key-response.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  readonly #APP_CONFIG = inject(APP_CONFIG);
  readonly #ENV_CONFIG = inject(ENV_CONFIG);
  readonly #httpClient = inject(HttpClient);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #cookie = inject(CookieService);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #subscriptions = new Subscription();
  readonly #toastr = inject(BnToastrService);
  readonly #posthog = inject(PosthogService);
  readonly #isUserLoggedIn = signal(false);
  readonly #destroyed$ = new Subject(); // use with takeUntil to unsubscribe from observables
  readonly #stopPolling = new Subject<boolean>();
  readonly #apiUrl!: string;
  readonly authStore = new BehaviorSubject<AuthStorage | null>(null);

  // TODO (ng19): Expose readonly signal like: `readOnly(this.#isUserLoggedIn)`
  // Exposed passive readonly logged in status
  readonly isUserLoggedIn = computed(() => this.#isUserLoggedIn());

  private readonly onLoginStateChange$ = new Subject<boolean | string>();
  private readonly storeKey = '🐇-access-to-🥕';
  private readonly blurThumbnailsLSKey = 'blurThumbnails';

  constructor() {
    this.#apiUrl = `${this.#ENV_CONFIG.apiUrl}/auth`;

    this.isLoggedIn();
    this.handleRefreshTokenInterval();
  }

  ngOnDestroy(): void {
    this.#stopPolling.next(true);
    this.#stopPolling.complete();

    this.#destroyed$.next(true);
    this.#destroyed$.complete();

    this.#subscriptions.unsubscribe();
  }

  public isSubuser(authStorage?: AuthStorage): boolean {
    return authStorage?.user?.Roles?.includes('Subuser') || false;
  }

  public getAuthStore(): Observable<AuthStorage | null> {
    return this.authStore.asObservable();
  }

  getAuthStoreSignal(): Signal<AuthStorage | null> {
    return toSignal(this.authStore.asObservable(), { initialValue: null });
  }

  public getLoginSuccess$(): Observable<void> {
    return this.onLoginStateChange$.asObservable().pipe(
      filter((value) => !!value),
      map(() => void 0),
    );
  }

  public getLogoutSuccess$(): Observable<boolean | string> {
    return this.onLoginStateChange$.asObservable().pipe(
      filter((value) => value === false || (typeof value === 'string' && value.length > 0)),
      map((value: string | boolean) => (typeof value === 'string' ? value : false)),
    );
  }

  public get authStoreValue(): AuthStorage | null {
    return this.authStore.value;
  }

  public getApiKey(): Observable<ApiKeyResponse> {
    return this.#httpClient.get<ApiKeyResponse>(`${this.#ENV_CONFIG.apiUrl}/apikey`);
  }

  public login(Email: string, Password: string, Utm?: UtmData, isExtAuthMode = false): Observable<AuthStorage> {
    return this.#httpClient
      .post<UserAuth>(`${this.#apiUrl}/jwt`, { Email, Password, Utm })
      .pipe(map((authResult: UserAuth) => this.handleAuthResponse(authResult, isExtAuthMode)));
  }

  public tokenLogin(token: string): Observable<UserAuth | false> {
    if (!token) {
      return of(false);
    }

    // Try to decode the token and refresh it. If anything goes wrong authentication failed.
    try {
      // We get base64 encoded jwt token
      const decodedToken = atob(token);
      const userAuth: UserAuth = {
        AuthStatus: this.getTokenAuthStatus(decodedToken),
        Token: decodedToken,
        Message: '',
      };

      this.handleAuthResponse(userAuth);

      // Refresh token to validate it actually works
      return this.#httpClient.post<UserAuth>(`${this.#apiUrl}/jwt/refresh`, {});
    } catch {
      // Authentication failed, return false
    }

    return of(false);
  }

  public verifyTwoFactorAuth(Code: string): Observable<AuthStorage> {
    return this.#httpClient.post<UserAuth>(`${this.#apiUrl}/jwt/2fa`, { Code }).pipe(map((authResult: UserAuth) => this.handleAuthResponse(authResult)));
  }

  public logout(errorMessage?: string, emitLogoutEvent = true): void {
    localStorage.removeItem(this.storeKey);
    localStorage.removeItem(this.blurThumbnailsLSKey);
    this.authStore.next(null);
    this.#stopPolling.next(true);
    this.#subscriptions.unsubscribe();
    this.#isUserLoggedIn.set(false);

    this.#posthog.reset();

    // NOTE: emitLogoutEvent = redirect to login page from app.component
    if (emitLogoutEvent) this.onLoginStateChange$.next(errorMessage || false);
  }

  // Use if logged in status needs to be checked in depth
  isLoggedIn(): boolean {
    const nowTimestamp = DateTimeUtils.currentEpoch();
    const { localData } = this.getAuthData();
    const { auth_token, auth_status, expires_at } = localData;

    if (auth_status === AuthStatus.Authenticated && auth_token && expires_at && nowTimestamp < Number(expires_at)) {
      if (!this.authStore.value) {
        this.authStore.next(localData);
      }

      this.#isUserLoggedIn.set(true);
      return true;
    }

    localStorage.removeItem(this.storeKey);
    this.#isUserLoggedIn.set(false);
    return false;
  }

  public refreshToken(): Observable<boolean> {
    return this.#httpClient.post<UserAuth>(`${this.#apiUrl}/jwt/refresh`, {}).pipe(
      map((authResult: UserAuth) => {
        this.handleAuthResponse(authResult);

        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        if ([401, 403].includes(error.status)) {
          this.logout();
        }

        return of(false);
      }),
    );
  }

  public register(Email: string, Password: string, Utm?: UtmData, AffiliateCode?: string, PowToken?: string): Observable<null> {
    return this.#httpClient.post<null>(`${this.#apiUrl}/register`, {
      AffiliateCode,
      PowToken,
      Email,
      Password,
      Utm,
    });
  }

  public resetPassword(Email: string, CallbackUrl: string): Observable<null> {
    return this.#httpClient.post<null>(`${this.#apiUrl}/resetpassword`, {
      Email,
      CallbackUrl,
    });
  }

  public resetPasswordConfirm(Email: string, Password: string, Code: string): Observable<null> {
    return this.#httpClient.post<null>(`${this.#apiUrl}/resetpassword/confirm`, {
      Email,
      Password,
      Code,
    });
  }

  public changePassword(OldPassword: string, NewPassword: string): Observable<null> {
    return this.#httpClient.post<null>(`${this.#apiUrl}/changepassword`, {
      OldPassword,
      NewPassword,
    });
  }

  public onBrowserTabFocus(): void {
    const { localData } = this.getAuthData();

    if (localData.auth_status && !this.isLoggedIn()) {
      this.logout();
    }
  }

  public getAuthStoreKey(): string {
    return this.storeKey;
  }

  public navigateAfterLogin(url = ''): Observable<boolean> {
    this.onLoginStateChange$.next(true);
    return from(this.#router.navigateByUrl(url));
  }

  public invokeConvertToSSO(provider: string): Observable<void> {
    return this.#httpClient.post<void>(`${this.#apiUrl}/converttosso/${provider}`, {}).pipe(
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  public getApiKeyValue(): Observable<string | undefined> {
    return this.getApiKey().pipe(
      switchMap((data: { Items: { Key: string }[] }) => {
        if (!data.Items[0]?.Key) {
          this.#toastr.danger(`Can't get the API key.`);
          return of(undefined);
        }

        return of(data.Items[0].Key);
      }),
    );
  }

  public getCallbackUrl(apiKey?: string, url?: string): string {
    const callbackUrl = this.#sanitizer.sanitize(SecurityContext.URL, url ?? this.#route.snapshot.queryParams['callbackUrl']) ?? '/';

    return apiKey ? this.enrichUrlWithAPIKey(callbackUrl, apiKey) : callbackUrl;
  }

  public enrichUrlWithAPIKey(url: string, apiKey: string): string {
    const urlObject = new URL(url);
    const urlObjectQueryParams = new URLSearchParams(urlObject.search);
    urlObjectQueryParams.append('apiKey', apiKey);
    urlObject.search = urlObjectQueryParams.toString();

    return urlObject.toString();
  }

  public removeCookieAndRedirect(redirectUrl: string): void {
    this.#cookie.remove(this.#APP_CONFIG.extAuthCookieName);

    window.location.replace(redirectUrl);
  }

  private handleRefreshTokenInterval(): void {
    const jwtInterval = DateTimeUtils.getDurationAsMs(...this.#APP_CONFIG.api.refreshInterval.jwt);

    // Refresh immediately to get new data after page reload
    this.#subscriptions.add(
      timer(DateTimeUtils.getDurationAsMs(0.5, 's'), jwtInterval)
        .pipe(
          takeUntil(this.#stopPolling),
          exhaustMap(() => (this.isLoggedIn() ? this.refreshToken() : EMPTY)),
        )
        .subscribe(),
    );
  }

  private getAuthData(): { [key: string]: AuthStorage } {
    const localData: AuthStorage = JSON.parse(localStorage.getItem(this.storeKey) ?? '{}');

    return { localData };
  }

  private handleAuthResponse(userAuth: UserAuth, isExtAuthMode = false): AuthStorage {
    const token = userAuth.Token;
    const authStatus = userAuth.AuthStatus ?? AuthStatus.Invalid;
    const message = userAuth.Message;

    if (!token) {
      this.#toastr.danger(message);

      return { auth_token: null, auth_status: AuthStatus.Invalid };
    }

    const decodedToken: UserJWT = jwtDecode(token);
    const authStoreData: AuthStorage = {
      auth_token: token ?? null,
      auth_status: authStatus,
      expires_at: decodedToken.exp,
      user: decodedToken.user,
    };

    // disallow subuser login at external wordpress authorisation
    if (isExtAuthMode && this.isSubuser(authStoreData)) {
      return {
        auth_token: null,
        auth_status: AuthStatus.Invalid,
        isSubuser: true,
      };
    }

    if (decodedToken.agent) authStoreData.agent = decodedToken.agent;
    if (decodedToken.partner) authStoreData.partner = decodedToken.partner;

    this.authStore.next(token ? authStoreData : null);

    // Only write local storage if authenticated
    if (authStatus === AuthStatus.Authenticated) {
      localStorage.setItem(this.storeKey, JSON.stringify(authStoreData));
    }

    return authStoreData;
  }

  private getTokenAuthStatus(token: string): AuthStatus {
    const decodedToken: UserJWT = jwtDecode(token);

    if (decodedToken.user && 'Email' in decodedToken.user) return AuthStatus.Authenticated;

    return AuthStatus.TwoFactorRequired;
  }
}
